import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { TOAST_LAYER } from "@xsolla/xui-core";
import { Toast, type ToastProps, type ToastType } from "./Toast";

export type ToastPosition =
  | "top-right"
  | "top-left"
  | "top-center"
  | "bottom-right"
  | "bottom-left"
  | "bottom-center";

export type ToastVariant = "default" | "sonner";

export interface ToastOptions extends Omit<
  ToastProps,
  "type" | "onClose" | "testID"
> {
  /**
   * Auto-dismiss duration in ms. Pass 0 to disable auto-dismiss.
   * Defaults derived from `type`: success 4500, info/warning 6500, alert 9000.
   */
  duration?: number;
  /** Stable id — if provided and a toast with that id already exists, it's replaced. */
  id?: string;
  /** Called when the toast is dismissed (auto or manual). */
  onDismiss?: () => void;
}

export interface ToastQueueItem extends ToastOptions {
  id: string;
  type: ToastType;
  createdAt: number;
}

export interface ToastApi {
  alert: (options: ToastOptions) => string;
  success: (options: ToastOptions) => string;
  warning: (options: ToastOptions) => string;
  info: (options: ToastOptions) => string;
  show: (type: ToastType, options: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

interface ToastContextValue {
  api: ToastApi;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4500,
  info: 6500,
  warning: 6500,
  alert: 9000,
};

const POSITION_STYLES: Record<ToastPosition, React.CSSProperties> = {
  "top-right": { top: 16, right: 16, alignItems: "flex-end" },
  "top-left": { top: 16, left: 16, alignItems: "flex-start" },
  "top-center": {
    top: 16,
    left: "50%",
    transform: "translateX(-50%)",
    alignItems: "center",
  },
  "bottom-right": { bottom: 16, right: 16, alignItems: "flex-end" },
  "bottom-left": { bottom: 16, left: 16, alignItems: "flex-start" },
  "bottom-center": {
    bottom: 16,
    left: "50%",
    transform: "translateX(-50%)",
    alignItems: "center",
  },
};

export interface ToastProviderProps {
  children: React.ReactNode;
  /** Where toasts appear on screen. Defaults to top-right. */
  position?: ToastPosition;
  /** Maximum number of simultaneously visible toasts. Defaults to 3. */
  limit?: number;
  /** DOM node to portal the viewport into. Defaults to document.body. */
  container?: HTMLElement | null;
  /**
   * Enable enter & exit animations. Defaults to `true`.
   * When `false`, toasts appear and disappear instantly.
   */
  animated?: boolean;
  /** Animation duration in ms. Defaults to 220 (default variant) / 400 (sonner). */
  animationDuration?: number;
  /**
   * Visual variant.
   * - `"default"` — toasts slide in from the edge and stack vertically.
   * - `"sonner"` — shadcn/sonner-style stacked-cards: toasts overlap as a deck
   *   with back cards scaled & peeking; hovering the stack expands it.
   */
  variant?: ToastVariant;
  testID?: string;
}

// Off-screen transform used as the enter start / exit end state, derived
// from the viewport position (slides in from the closest edge).
const enterTransform = (position: ToastPosition): string => {
  if (position.endsWith("right")) return "translateX(110%)";
  if (position.endsWith("left")) return "translateX(-110%)";
  return position.startsWith("top") ? "translateY(-110%)" : "translateY(110%)";
};

// CSS to anchor an absolutely-positioned sonner item to the viewport corner.
const SONNER_ANCHOR: Record<ToastPosition, React.CSSProperties> = {
  "top-right": { top: 0, right: 0 },
  "top-left": { top: 0, left: 0 },
  "top-center": { top: 0, left: "50%", marginLeft: -220 },
  "bottom-right": { bottom: 0, right: 0 },
  "bottom-left": { bottom: 0, left: 0 },
  "bottom-center": { bottom: 0, left: "50%", marginLeft: -220 },
};

const SONNER_GAP = 8;
const SONNER_PEEK = 6;
const SONNER_SCALE_STEP = 0.06;
const MAX_VISIBLE_BACK = 2; // beyond this index, toasts fade to zero

interface ToastItemProps {
  item: ToastQueueItem;
  position: ToastPosition;
  variant: ToastVariant;
  animated: boolean;
  animationDuration: number;
  exiting: boolean;
  // Sonner-specific
  frontIndex: number;
  expanded: boolean;
  cumulativeOffset: number;
  reportHeight: (id: string, h: number) => void;
  // Common
  onClose: () => void;
  onPause: () => void;
  onResume: () => void;
  onHoverIn: (id: string) => void;
  onHoverOut: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({
  item,
  position,
  variant,
  animated,
  animationDuration,
  exiting,
  frontIndex,
  expanded,
  cumulativeOffset,
  reportHeight,
  onClose,
  onPause,
  onResume,
  onHoverIn,
  onHoverOut,
}) => {
  const [entered, setEntered] = useState(!animated);
  const ref = useRef<HTMLDivElement | null>(null);
  const hoveredRef = useRef(false);

  useEffect(() => {
    if (!animated) return;
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [animated]);

  // Guarantee hover state is cleaned up if the item unmounts while hovered
  // (mouseLeave doesn't fire on disappearing elements).
  useEffect(
    () => () => {
      if (hoveredRef.current) {
        hoveredRef.current = false;
        onHoverOut(item.id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Measure height for sonner stacking math.
  useLayoutEffect(() => {
    if (variant !== "sonner" || !ref.current) return;
    const el = ref.current;
    const report = () =>
      reportHeight(item.id, el.getBoundingClientRect().height);
    report();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [variant, item.id, reportHeight]);

  const isSonner = variant === "sonner";
  const isBottom = position.startsWith("bottom");
  const isCenter = position.endsWith("center");
  const dir = isBottom ? -1 : 1; // direction items move to "stack back"

  let transform: string;
  let opacity = 1;
  let zIndex = 1000 - frontIndex;

  const offTransform = enterTransform(position);
  const showOff = animated && (!entered || exiting);

  if (isSonner) {
    // Horizontal centering baseline for *-center positions
    const xBase = ""; // marginLeft handles centering for sonner-anchored items

    if (showOff) {
      transform = `${xBase} ${offTransform}`.trim();
      opacity = 0;
    } else if (frontIndex === 0) {
      transform = `${xBase} translateY(0) scale(1)`;
    } else if (expanded) {
      // Spread out using measured cumulative offsets
      transform = `${xBase} translateY(${dir * cumulativeOffset}px) scale(1)`;
    } else {
      // Collapsed stack: peek behind front, scaled down
      const peek = SONNER_PEEK * frontIndex;
      const scale = Math.max(0.7, 1 - SONNER_SCALE_STEP * frontIndex);
      transform = `${xBase} translateY(${dir * peek}px) scale(${scale})`;
      if (frontIndex > MAX_VISIBLE_BACK) opacity = 0;
    }
  } else {
    // Default variant: simple slide-in / slide-out
    transform = showOff ? offTransform : "translateX(0) translateY(0)";
    opacity = showOff ? 0 : 1;
  }

  const style: React.CSSProperties = {
    pointerEvents: opacity === 0 ? "none" : "auto",
    transform,
    opacity,
    zIndex,
    transition: animated
      ? `transform ${animationDuration}ms cubic-bezier(0.16, 1, 0.3, 1), opacity ${animationDuration}ms ease`
      : undefined,
    willChange: animated ? "transform, opacity" : undefined,
    transformOrigin: isSonner
      ? isCenter
        ? isBottom
          ? "bottom center"
          : "top center"
        : isBottom
          ? `bottom ${position.endsWith("right") ? "right" : "left"}`
          : `top ${position.endsWith("right") ? "right" : "left"}`
      : undefined,
    ...(isSonner
      ? {
          position: "absolute" as const,
          width: 440,
          ...SONNER_ANCHOR[position],
        }
      : null),
  };

  return (
    <div
      ref={ref}
      style={style}
      onMouseEnter={() => {
        onPause();
        if (!hoveredRef.current) {
          hoveredRef.current = true;
          onHoverIn(item.id);
        }
      }}
      onMouseLeave={() => {
        onResume();
        if (hoveredRef.current) {
          hoveredRef.current = false;
          onHoverOut(item.id);
        }
      }}
      // Ignore focus moves between descendants (e.g. action → close button) —
      // those are still inside the toast and shouldn't toggle the timer.
      onFocus={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        onPause();
        if (!hoveredRef.current) {
          hoveredRef.current = true;
          onHoverIn(item.id);
        }
      }}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        onResume();
        if (hoveredRef.current) {
          hoveredRef.current = false;
          onHoverOut(item.id);
        }
      }}
    >
      <Toast
        type={item.type}
        title={item.title}
        description={item.description}
        action={item.action}
        showCloseButton={item.showCloseButton}
        onClose={onClose}
        themeMode={item.themeMode}
        themeProductContext={item.themeProductContext}
      />
    </div>
  );
};

let idCounter = 0;
const nextId = () => `toast-${Date.now()}-${++idCounter}`;

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "top-right",
  limit = 3,
  container,
  animated = true,
  animationDuration,
  variant = "default",
  testID,
}) => {
  const isSonner = variant === "sonner";
  const effectiveDuration = animationDuration ?? (isSonner ? 400 : 220);

  const [toasts, setToasts] = useState<ToastQueueItem[]>([]);
  const [exiting, setExiting] = useState<Set<string>>(() => new Set());
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [hoveredIds, setHoveredIds] = useState<Set<string>>(() => new Set());
  const exitTimeoutsRef = useRef<Map<string, number>>(new Map());
  const timersRef = useRef<
    Map<string, { remaining: number; startedAt: number; timeoutId: number }>
  >(new Map());
  const onDismissRef = useRef<Map<string, () => void>>(new Map());
  const dismissRef = useRef<(id: string) => void>(() => {});

  const reportHeight = useCallback((id: string, h: number) => {
    setHeights((prev) => (prev[id] === h ? prev : { ...prev, [id]: h }));
  }, []);

  const clearTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (entry) {
      window.clearTimeout(entry.timeoutId);
      timersRef.current.delete(id);
    }
  }, []);

  const removeNow = useCallback((id: string) => {
    const cb = onDismissRef.current.get(id);
    onDismissRef.current.delete(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
    setExiting((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setHeights((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setHoveredIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    cb?.();
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      if (!animated) {
        removeNow(id);
        return;
      }
      if (exitTimeoutsRef.current.has(id)) return;
      setExiting((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      const timeoutId = window.setTimeout(() => {
        exitTimeoutsRef.current.delete(id);
        removeNow(id);
      }, effectiveDuration);
      exitTimeoutsRef.current.set(id, timeoutId);
    },
    [clearTimer, animated, effectiveDuration, removeNow]
  );

  useEffect(() => {
    dismissRef.current = dismiss;
  }, [dismiss]);

  const startTimer = useCallback((id: string, duration: number) => {
    if (duration <= 0) return;
    const timeoutId = window.setTimeout(() => {
      dismissRef.current(id);
    }, duration);
    timersRef.current.set(id, {
      remaining: duration,
      startedAt: Date.now(),
      timeoutId,
    });
  }, []);

  const pauseTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (!entry) return;
    const elapsed = Date.now() - entry.startedAt;
    const remaining = Math.max(0, entry.remaining - elapsed);
    window.clearTimeout(entry.timeoutId);
    timersRef.current.set(id, {
      remaining,
      startedAt: Date.now(),
      timeoutId: 0,
    });
  }, []);

  const resumeTimer = useCallback(
    (id: string) => {
      const entry = timersRef.current.get(id);
      if (!entry || entry.timeoutId !== 0) return;
      startTimer(id, entry.remaining);
    },
    [startTimer]
  );

  // Fire any registered onDismiss for `id` and clear all per-toast bookkeeping.
  // Used when a toast is replaced (same id re-issued) or evicted by limit
  // overflow — both are documented dismissal paths.
  const cleanupDroppedToast = useCallback(
    (id: string) => {
      clearTimer(id);
      const exitTimeoutId = exitTimeoutsRef.current.get(id);
      if (exitTimeoutId) {
        window.clearTimeout(exitTimeoutId);
        exitTimeoutsRef.current.delete(id);
      }
      setExiting((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      const cb = onDismissRef.current.get(id);
      onDismissRef.current.delete(id);
      cb?.();
    },
    [clearTimer]
  );

  const show = useCallback(
    (type: ToastType, options: ToastOptions): string => {
      const id = options.id ?? nextId();
      const duration = options.duration ?? DEFAULT_DURATIONS[type];
      const item: ToastQueueItem = {
        ...options,
        id,
        type,
        createdAt: Date.now(),
      };

      // If this id is currently exiting (replacement during exit animation),
      // cancel the pending exit so the new toast doesn't get marked exiting
      // and the old timeout doesn't remove it.
      const pendingExit = exitTimeoutsRef.current.get(id);
      if (pendingExit) {
        window.clearTimeout(pendingExit);
        exitTimeoutsRef.current.delete(id);
        setExiting((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }

      // Manage the onDismiss slot atomically: replacing without a new
      // callback should not leave the previous one registered.
      if (options.onDismiss) {
        onDismissRef.current.set(id, options.onDismiss);
      } else {
        onDismissRef.current.delete(id);
      }

      setToasts((prev) => {
        const replaced = prev.filter((t) => t.id === id);
        const withoutDup = prev.filter((t) => t.id !== id);
        const next = [...withoutDup, item];
        const overflow =
          next.length > limit ? next.slice(0, next.length - limit) : [];

        // Replaced toasts: their onDismiss has already been overwritten
        // above, so just clear timers (no callback to fire).
        replaced.forEach((t) => {
          clearTimer(t.id);
          const ex = exitTimeoutsRef.current.get(t.id);
          if (ex) {
            window.clearTimeout(ex);
            exitTimeoutsRef.current.delete(t.id);
          }
        });

        // Overflow toasts: fire onDismiss and clean up.
        overflow.forEach((t) => {
          if (t.id === id) return; // never the freshly-added one
          cleanupDroppedToast(t.id);
        });

        return next.length > limit ? next.slice(next.length - limit) : next;
      });

      clearTimer(id);
      startTimer(id, duration);

      return id;
    },
    [clearTimer, startTimer, limit, cleanupDroppedToast]
  );

  const dismissAll = useCallback(() => {
    const ids = Array.from(timersRef.current.keys());
    ids.forEach((id) => dismiss(id));
    setToasts((prev) => {
      prev.forEach((t) => {
        if (!ids.includes(t.id)) dismiss(t.id);
      });
      return prev;
    });
  }, [dismiss]);

  const api = useMemo<ToastApi>(
    () => ({
      alert: (options) => show("alert", options),
      success: (options) => show("success", options),
      warning: (options) => show("warning", options),
      info: (options) => show("info", options),
      show,
      dismiss,
      dismissAll,
    }),
    [show, dismiss, dismissAll]
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((entry) =>
        window.clearTimeout(entry.timeoutId)
      );
      timersRef.current.clear();
      exitTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
      exitTimeoutsRef.current.clear();
    },
    []
  );

  const portalTarget =
    container ?? (typeof document !== "undefined" ? document.body : null);

  const isBottom = position.startsWith("bottom");

  // Build the render list with frontIndex (0 = newest = front of stack).
  // toasts[] is oldest → newest. For default variant the visual order is:
  //   top-*: newest first in DOM (so it appears at the top edge)
  //   bottom-*: newest last in DOM (so it appears at the bottom edge)
  // For sonner the DOM order doesn't affect visual stacking (all absolute,
  // controlled by z-index), but for tab order we keep front-first in DOM.
  const orderedItems = (() => {
    if (isSonner) {
      // newest first
      return [...toasts].reverse().map((item, i) => ({ item, frontIndex: i }));
    }
    // default
    if (isBottom) {
      // newest last → frontIndex 0 is the last item
      return toasts.map((item, i) => ({
        item,
        frontIndex: toasts.length - 1 - i,
      }));
    }
    return [...toasts].reverse().map((item, i) => ({ item, frontIndex: i }));
  })();

  // Cumulative offset used by sonner-expanded layout: sum of heights of items
  // closer to the front (smaller frontIndex) plus gap.
  const offsetByFrontIndex = useMemo(() => {
    const map: Record<number, number> = { 0: 0 };
    let cumulative = 0;
    // Walk in front-first order.
    const sorted = [...orderedItems].sort(
      (a, b) => a.frontIndex - b.frontIndex
    );
    for (let i = 0; i < sorted.length; i++) {
      const fi = sorted[i].frontIndex;
      if (fi === 0) continue;
      const prev = sorted[i - 1];
      cumulative += (heights[prev.item.id] ?? 64) + SONNER_GAP;
      map[fi] = cumulative;
    }
    return map;
  }, [orderedItems, heights]);

  const expanded = isSonner && hoveredIds.size > 0;

  // For sonner: keep the viewport sized so it captures hover even when only
  // the front toast is visible.
  const frontHeight = (() => {
    const front = orderedItems.find((x) => x.frontIndex === 0);
    return front ? (heights[front.item.id] ?? 64) : 0;
  })();

  const sonnerExpandedHeight =
    frontHeight +
    orderedItems
      .filter((x) => x.frontIndex > 0)
      .reduce((sum, x) => sum + (heights[x.item.id] ?? 64) + SONNER_GAP, 0);

  return (
    <ToastContext.Provider value={{ api }}>
      {children}
      {portalTarget &&
        createPortal(
          <div
            data-toast-viewport=""
            data-testid={testID}
            data-variant={variant}
            data-expanded={expanded ? "" : undefined}
            style={{
              position: "fixed",
              zIndex: TOAST_LAYER,
              pointerEvents: "none",
              maxWidth: "calc(100vw - 32px)",
              ...(isSonner
                ? {
                    width: 440,
                    height: expanded ? sonnerExpandedHeight : frontHeight,
                    transition: animated
                      ? `height ${effectiveDuration}ms cubic-bezier(0.16, 1, 0.3, 1)`
                      : undefined,
                    ...POSITION_STYLES[position],
                    // override flex (we use absolute children in sonner mode)
                    display: "block",
                  }
                : {
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    ...POSITION_STYLES[position],
                  }),
            }}
          >
            {orderedItems.map(({ item, frontIndex }) => (
              <ToastItem
                key={item.id}
                item={item}
                position={position}
                variant={variant}
                animated={animated}
                animationDuration={effectiveDuration}
                exiting={exiting.has(item.id)}
                frontIndex={frontIndex}
                expanded={expanded}
                cumulativeOffset={offsetByFrontIndex[frontIndex] ?? 0}
                reportHeight={reportHeight}
                onClose={() => dismiss(item.id)}
                onPause={() => pauseTimer(item.id)}
                onResume={() => resumeTimer(item.id)}
                onHoverIn={(id) => {
                  if (!isSonner) return;
                  setHoveredIds((prev) => {
                    if (prev.has(id)) return prev;
                    const next = new Set(prev);
                    next.add(id);
                    return next;
                  });
                }}
                onHoverOut={(id) => {
                  if (!isSonner) return;
                  setHoveredIds((prev) => {
                    if (!prev.has(id)) return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                  });
                }}
              />
            ))}
          </div>,
          portalTarget
        )}
    </ToastContext.Provider>
  );
};

ToastProvider.displayName = "ToastProvider";

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider />");
  }
  return ctx.api;
};
